/**
 * Карта на MapLibre GL JS поверх растровых тайлов OpenStreetMap.
 *
 * Ключей и платных API здесь нет: базовый слой — обычные png-тайлы. Своё
 * рисуется поверх двумя GeoJSON-источниками (линии маршрута и фоновые точки)
 * и DOM-маркерами для того, по чему надо попадать пальцем. Императивная
 * работа заперта внутри компонента: React отдаёт данные, маркеры живут своей
 * жизнью — иначе карта пересобиралась бы на каждый кадр движения шторки.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import type {
  ExpressionSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
  Marker as MapLibreMarker,
  PaddingOptions,
  StyleSpecification,
} from 'maplibre-gl';

import type { Car, GasStation, LatLon, Plan } from '@cdf/shared';

import { TIER_VAR, carTier } from '../lib/carTier';

import 'maplibre-gl/dist/maplibre-gl.css';

type MapLibreModule = typeof import('maplibre-gl');

/** Свойства линии маршрута — по ним слой сам выбирает цвет и толщину. */
interface LegProperties {
  done: boolean;
  active: boolean;
  estimated: boolean;
}

type RouteData = FeatureCollection<LineString, LegProperties>;

/**
 * Фоновые точки — АЗС и машины вне цепочки. Их бывает под полтысячи, и
 * DOM-маркер на каждую кладёт телефон: браузер пересчитывает transform всем
 * элементам на каждом кадре карты. Поэтому фон рисует GPU одним слоем.
 */
interface DotProperties {
  station: boolean;
  color: string;
}

type DotData = FeatureCollection<Point, DotProperties>;

const ROUTE_SOURCE = 'route';
const ROUTE_SOLID = 'route-solid';
const ROUTE_DASHED = 'route-dashed';
const DOTS_SOURCE = 'dots';
const DOTS_STATION = 'dots-station';
const DOTS_CAR = 'dots-car';

/** Дальше 18 растровые тайлы OSM просто не существуют. */
const MAX_ZOOM = 18;
/** Ниже 8 карта бесполезна: город целиком уже не помещается в задачу. */
const MIN_ZOOM = 8;

const MOSCOW: LatLon = { lat: 55.7522, lon: 37.6156 };

const OSM_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer noopener">OpenStreetMap contributors</a>';

export interface MapHandle {
  /** Показать точку пользователя. */
  centerOn(point: LatLon, zoom?: number): void;
  /** Вписать в экран всю цепочку. */
  fitPlan(): void;
}

export interface MapViewProps {
  user: LatLon | null;
  cars: Car[];
  plan: Plan | null;
  stations: GasStation[];
  activeIndex: number | null;
  alertIndices: ReadonlySet<number>;
  doneIndices: ReadonlySet<number>;
  /** Порог литров из настроек — от него считается цвет маркера. */
  minLitersForBonus: number;
  /** Высота видимой части шторки — карта смещает центр вверх на это значение. */
  bottomPadding: number;
  /** Режим «ткни в карту, чтобы задать старт». */
  pickMode: boolean;
  onPick(point: LatLon): void;
  onSelectStop(index: number): void;
  onFailure(reason: string): void;
}

const toLngLat = (point: LatLon): [number, number] => [point.lon, point.lat];

function cssColor(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

/** Тёмная тема — база приложения, светлая включается только явным запросом. */
function prefersLight(): boolean {
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}

function buildStyle(light: boolean): StyleSpecification {
  // CARTO раздаёт тайлы с четырёх поддоменов — браузер качает их параллельно.
  const tiles = light
    ? ['https://tile.openstreetmap.org/{z}/{x}/{y}.png']
    : ['a', 'b', 'c', 'd'].map(
        (sub) => `https://${sub}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png`,
      );

  return {
    version: 8,
    // Ни glyphs, ни sprite не нужны: подписей на растре нет, свои значки — DOM.
    sources: {
      basemap: {
        type: 'raster',
        tiles,
        tileSize: 256,
        minzoom: 0,
        maxzoom: MAX_ZOOM,
        attribution: light
          ? OSM_ATTRIBUTION
          : `${OSM_ATTRIBUTION} © <a href="https://carto.com/attributions" target="_blank" rel="noreferrer noopener">CARTO</a>`,
      },
    },
    layers: [
      {
        id: 'bg',
        type: 'background',
        paint: { 'background-color': cssColor('--c-bg-deep', '#05080b') },
      },
      { id: 'basemap', type: 'raster', source: 'basemap' },
    ],
  };
}

/**
 * Отступы камеры: сверху панель со статусом, снизу шторка. Свободной полосе
 * не даём схлопнуться в ноль — на раскрытой шторке карта иначе теряет центр.
 */
function cameraPadding(height: number, bottom: number): PaddingOptions {
  const top = 88;
  const maxBottom = Math.max(0, height - top - 120);
  return {
    top,
    bottom: Math.round(Math.min(Math.max(bottom, 0), maxBottom)),
    left: 24,
    right: 24,
  };
}

function buildRouteData(
  plan: Plan | null,
  activeIndex: number | null,
  doneIndices: ReadonlySet<number>,
): RouteData {
  const features: Array<Feature<LineString, LegProperties>> = [];

  for (const stop of plan?.stops ?? []) {
    const done = doneIndices.has(stop.index);
    const active = stop.index === activeIndex;
    for (const leg of stop.legs) {
      if (leg.kind === 'refuel') continue;
      // Точной геометрии может не быть — тогда рисуем прямую «как летит ворон».
      const coordinates =
        leg.geometry && leg.geometry.length > 1
          ? leg.geometry.map(toLngLat)
          : [toLngLat(leg.from), toLngLat(leg.to)];

      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates },
        properties: { done, active, estimated: leg.estimated },
      });
    }
  }

  return { type: 'FeatureCollection', features };
}

function buildDotData(
  cars: Car[],
  stations: GasStation[],
  plan: Plan | null,
  minLitersForBonus: number,
): DotData {
  const features: Array<Feature<Point, DotProperties>> = [];

  const stationColor = cssColor('--c-text-2', '#a3b1c2');
  for (const station of stations) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: toLngLat(station.location) },
      properties: { station: true, color: stationColor },
    });
  }

  // Машины из цепочки уже нарисованы булавками — дублировать их точкой незачем.
  const plannedIds = new Set((plan?.stops ?? []).map((stop) => stop.car.id));
  for (const car of cars) {
    if (plannedIds.has(car.id)) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: toLngLat(car.location) },
      properties: {
        station: false,
        color: cssColor(TIER_VAR[carTier(car, minLitersForBonus)], '#64748b'),
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

function pinElement(
  label: string,
  tierVar: string,
  modifiers: string,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `mk mk--pin${modifiers}`;
  button.style.setProperty('--mk-color', `var(${tierVar})`);

  const body = document.createElement('span');
  body.className = 'mk__pin';
  const num = document.createElement('span');
  num.className = 'mk__num';
  num.textContent = label;
  body.appendChild(num);
  button.appendChild(body);
  return button;
}

export const MapView = forwardRef<MapHandle, MapViewProps>(function MapView(
  {
    user,
    cars,
    plan,
    stations,
    activeIndex,
    alertIndices,
    doneIndices,
    minLitersForBonus,
    bottomPadding,
    pickMode,
    onPick,
    onSelectStop,
    onFailure,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const glRef = useRef<MapLibreModule | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<MapLibreMarker[]>([]);
  const userMarkerRef = useRef<MapLibreMarker | null>(null);
  const routeDataRef = useRef<RouteData>({ type: 'FeatureCollection', features: [] });
  const dotDataRef = useRef<DotData>({ type: 'FeatureCollection', features: [] });
  const styleReadyRef = useRef(false);
  const paddingRef = useRef<PaddingOptions>({ top: 88, bottom: 0, left: 24, right: 24 });
  const [ready, setReady] = useState(false);

  // Колбэки через ref: пересоздавать карту из-за смены обработчика недопустимо.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const onSelectStopRef = useRef(onSelectStop);
  onSelectStopRef.current = onSelectStop;
  const pickModeRef = useRef(pickMode);
  pickModeRef.current = pickMode;
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;
  const initialCenter = useRef<LatLon | null>(user);
  if (initialCenter.current === null && user) initialCenter.current = user;

  /** Свои слои живут вне стиля: после смены темы их надо вернуть на место. */
  const applyOverlay = useCallback((map: MapLibreMap) => {
    if (!styleReadyRef.current) return;

    const routeColor = cssColor('--c-accent', '#ffcc33');
    const doneColor = cssColor('--c-tier-off', '#64748b');
    const color: ExpressionSpecification = [
      'case',
      ['==', ['get', 'done'], true],
      doneColor,
      routeColor,
    ];
    const width: ExpressionSpecification = [
      'case',
      ['==', ['get', 'active'], true],
      7,
      5,
    ];

    const route = map.getSource(ROUTE_SOURCE) as GeoJSONSource | undefined;
    if (route) {
      void route.setData(routeDataRef.current).catch(() => undefined);
      // Цвета берутся из CSS-переменных, а они меняются вместе с темой.
      for (const layer of [ROUTE_SOLID, ROUTE_DASHED]) {
        if (!map.getLayer(layer)) continue;
        map.setPaintProperty(layer, 'line-color', color);
        map.setPaintProperty(layer, 'line-width', width);
      }
    } else {
      map.addSource(ROUTE_SOURCE, { type: 'geojson', data: routeDataRef.current });
      map.addLayer({
        id: ROUTE_SOLID,
        type: 'line',
        source: ROUTE_SOURCE,
        filter: ['!=', ['get', 'estimated'], true],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': color, 'line-width': width, 'line-opacity': 0.9 },
      });
      map.addLayer({
        id: ROUTE_DASHED,
        type: 'line',
        source: ROUTE_SOURCE,
        filter: ['==', ['get', 'estimated'], true],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: {
          'line-color': color,
          'line-width': width,
          'line-opacity': 0.9,
          'line-dasharray': [2, 1.6],
        },
      });
    }

    const dots = map.getSource(DOTS_SOURCE) as GeoJSONSource | undefined;
    if (dots) {
      void dots.setData(dotDataRef.current).catch(() => undefined);
      return;
    }

    map.addSource(DOTS_SOURCE, { type: 'geojson', data: dotDataRef.current });
    // АЗС — полое кольцо, машины — заливка цветом уровня: на глаз не спутать.
    map.addLayer({
      id: DOTS_STATION,
      type: 'circle',
      source: DOTS_SOURCE,
      filter: ['==', ['get', 'station'], true],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 2.5, 16, 5],
        // Заливку гасим полностью: кольцо на тайлах читается лучше пятна.
        'circle-opacity': 0,
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
      },
    });
    map.addLayer({
      id: DOTS_CAR,
      type: 'circle',
      source: DOTS_SOURCE,
      filter: ['!=', ['get', 'station'], true],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 16, 7],
        'circle-color': ['get', 'color'],
        'circle-opacity': 0.9,
        'circle-stroke-color': 'rgba(0, 0, 0, 0.4)',
        'circle-stroke-width': 1.5,
      },
    });
  }, []);

  // --- Инициализация карты (ровно один раз на жизнь компонента) ---
  useEffect(() => {
    let disposed = false;
    let watchdog = 0;
    // Карта создаётся асинхронно, а StrictMode прогоняет эффект дважды. Каждый
    // проход убирает за собой ровно свой экземпляр: снимать mapRef.current
    // нельзя — там уже может лежать карта следующего прохода.
    let owned: MapLibreMap | null = null;

    void (async () => {
      try {
        const maplibre = await import('maplibre-gl');
        if (disposed || !containerRef.current) return;

        const start = initialCenter.current ?? MOSCOW;
        const map = new maplibre.Map({
          container: containerRef.current,
          style: buildStyle(prefersLight()),
          center: toLngLat(start),
          zoom: 13,
          minZoom: MIN_ZOOM,
          maxZoom: MAX_ZOOM,
          // Наклон и поворот на телефоне только мешают: карта строго «на север».
          pitch: 0,
          dragRotate: false,
          touchPitch: false,
          // Атрибуция OSM/CARTO обязательна по лицензии, компактной она
          // занимает один значок в углу.
          attributionControl: { compact: true },
        });
        if (disposed) {
          map.remove();
          return;
        }
        map.touchZoomRotate.disableRotation();

        owned = map;
        (window as unknown as { __map: unknown }).__map = map;
        glRef.current = maplibre;
        mapRef.current = map;

        // Готовность считаем по стилю, а не по событию load: load ждёт ещё и
        // полной догрузки тайлов, а нам для маркеров хватает готового стиля.
        let styleLoaded = false;
        map.on('style.load', () => {
          styleLoaded = true;
          window.clearTimeout(watchdog);
          styleReadyRef.current = true;
          applyOverlay(map);
          if (!disposed) setReady(true);
        });

        map.on('click', (event) => {
          if (!pickModeRef.current) return;
          const { lat, lng } = event.lngLat;
          onPickRef.current({ lat, lon: lng });
        });

        // Упавший тайл посреди смены — обычное дело в дороге (тоннель, слабая
        // сеть), и рушить из-за него уже работающую карту нельзя. Сдаёмся,
        // только если не приехало вообще ничего.
        let earlyErrors = 0;
        map.on('error', (event) => {
          if (styleLoaded || disposed) return;
          earlyErrors += 1;
          if (earlyErrors >= 8) {
            onFailureRef.current(
              event.error instanceof Error ? event.error.message : 'тайлы не грузятся',
            );
          }
        });

        // Если движок завис на инициализации, список должен взять управление.
        watchdog = window.setTimeout(() => {
          if (!disposed && !styleLoaded) {
            onFailureRef.current('карта не успела загрузиться');
          }
        }, 15_000);
      } catch (error) {
        if (disposed) return;
        onFailureRef.current(
          error instanceof Error ? error.message : 'карта не загрузилась',
        );
      }
    })();

    return () => {
      disposed = true;
      window.clearTimeout(watchdog);
      if (!owned) return;

      for (const marker of markersRef.current) marker.remove();
      markersRef.current = [];
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      styleReadyRef.current = false;
      owned.remove();
      if (mapRef.current === owned) {
        mapRef.current = null;
        glRef.current = null;
      }
    };
  }, [applyOverlay]);

  // --- Тема карты следом за системной ---
  useEffect(() => {
    if (!ready) return;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const apply = (): void => {
      const map = mapRef.current;
      if (!map) return;
      // setStyle сносит наши слои — вернём их по событию style.load.
      styleReadyRef.current = false;
      map.setStyle(buildStyle(media.matches));
    };
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [ready]);

  // --- Отступ снизу: центр карты должен быть над шторкой ---
  useEffect(() => {
    if (!ready) return;
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container) return;

    const padding = cameraPadding(container.clientHeight, bottomPadding);
    paddingRef.current = padding;
    map.setPadding(padding);
    // Копирайт нельзя прятать под шторкой — поднимаем его вместе с ней.
    container.style.setProperty('--map-inset-bottom', `${padding.bottom}px`);
  }, [ready, bottomPadding]);

  const fitPlan = useCallback(() => {
    const gl = glRef.current;
    const map = mapRef.current;
    if (!gl || !map) return;

    const points: LatLon[] = [];
    if (user) points.push(user);
    for (const stop of plan?.stops ?? []) {
      points.push(stop.car.location, stop.station.location);
    }
    if (points.length === 0) return;

    const bounds = new gl.LngLatBounds();
    for (const point of points) bounds.extend(toLngLat(point));

    // Отступы под шапку и шторку уже стоят на камере через setPadding, а
    // fitBounds приплюсовывает своё поле к ним: сюда идёт только запас на
    // булавку, иначе сумма перекрывает холст и вписывание молча отменяется.
    map.fitBounds(bounds, { padding: 28, maxZoom: 16, duration: 450 });
  }, [plan, user]);

  useImperativeHandle(
    ref,
    () => ({
      centerOn(point, zoom = 15) {
        // easeTo учитывает padding, поэтому точка встаёт над шторкой, а не под ней.
        mapRef.current?.easeTo({
          center: toLngLat(point),
          zoom,
          duration: 350,
        });
      },
      fitPlan,
    }),
    [fitPlan],
  );

  // --- Перерисовка маркеров и линии маршрута ---
  useEffect(() => {
    const gl = glRef.current;
    const map = mapRef.current;
    if (!ready || !gl || !map) return;

    routeDataRef.current = buildRouteData(plan, activeIndex, doneIndices);
    dotDataRef.current = buildDotData(cars, stations, plan, minLitersForBonus);
    applyOverlay(map);

    for (const marker of markersRef.current) marker.remove();
    const markers: MapLibreMarker[] = [];

    // Шаги цепочки — нумерованные булавки поверх всего остального. Их десятки,
    // а не сотни, и по ним надо попадать пальцем, поэтому здесь DOM уместен.
    for (const stop of plan?.stops ?? []) {
      const tier = carTier(stop.car, minLitersForBonus);
      const isActive = stop.index === activeIndex;
      const modifiers = `${isActive ? ' is-active' : ''}${
        doneIndices.has(stop.index) ? ' is-done' : ''
      }${alertIndices.has(stop.index) ? ' is-alert' : ''}`;

      const node = pinElement(String(stop.index + 1), TIER_VAR[tier], modifiers);
      node.setAttribute('aria-label', `Шаг ${stop.index + 1}: ${stop.car.model}`);
      node.addEventListener('click', (event) => {
        // Иначе тап по булавке в pickMode ещё и переставит точку старта.
        event.stopPropagation();
        onSelectStopRef.current(stop.index);
      });

      markers.push(
        new gl.Marker({ element: node, anchor: 'bottom' })
          .setLngLat(toLngLat(stop.car.location))
          .addTo(map),
      );
    }

    markersRef.current = markers;
  }, [
    ready,
    plan,
    cars,
    stations,
    activeIndex,
    alertIndices,
    doneIndices,
    minLitersForBonus,
    applyOverlay,
  ]);

  // Точка пользователя живёт отдельно: она обновляется каждые пару секунд,
  // и пересобирать из-за неё весь слой маркеров было бы расточительно.
  useEffect(() => {
    const gl = glRef.current;
    const map = mapRef.current;
    if (!ready || !gl || !map) return;

    if (!user) {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      return;
    }

    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat(toLngLat(user));
      return;
    }

    const node = document.createElement('div');
    node.className = 'mk mk--user';
    node.style.pointerEvents = 'none';
    node.appendChild(document.createElement('span'));

    userMarkerRef.current = new gl.Marker({ element: node, anchor: 'center' })
      .setLngLat(toLngLat(user))
      .addTo(map);
  }, [ready, user]);

  // Новый план — показываем его целиком.
  useEffect(() => {
    if (ready && plan) fitPlan();
    // fitPlan меняется вместе с bottomPadding, а перескакивать при каждом
    // движении шторки не нужно — поэтому в зависимостях только id плана.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, plan?.id]);

  // Геопозиция приезжает уже после создания карты — доводим камеру до неё,
  // но только пока нет маршрута: у построенного плана свой кадр.
  const centeredRef = useRef(false);
  useEffect(() => {
    if (!ready || !user || centeredRef.current) return;
    centeredRef.current = true;
    if (plan) return;
    mapRef.current?.easeTo({ center: toLngLat(user), zoom: 14, duration: 400 });
    // Достаточно одного наведения на первую полученную точку.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  return (
    <div className={`map${pickMode ? ' map--picking' : ''}`}>
      <div className="map__canvas" ref={containerRef} />
      {pickMode ? (
        <div className="map__hint" role="status">
          Тапните по карте — там начнётся маршрут
        </div>
      ) : null}
    </div>
  );
});
